/**
 * E4 — how much reminder mail the engine currently has SCHEDULED.
 *
 * ⚠️ EVERY LABEL ON THIS CARD SAYS "SCHEDULED" AND NONE SAYS "WILL SEND", and
 * that is not pedantry. The engine re-checks each guest inside the transaction
 * that claims them, and the deadline pass consults the deadline gate before
 * every send — so a contributor who submits their chapter in the next ten
 * minutes is counted here and will never be written to. "38 reminders will send
 * in 24 hours" would be wrong the moment somebody writes something, which is
 * the outcome the reminder exists to cause. The server ships that sentence in
 * `definition.note` and this card renders it rather than paraphrasing it.
 *
 * ⚠️ `due_7d` INCLUDES `due_24h`. They are "inside the next N", not disjoint
 * bands, and adding them double-counts every guest in the first day. The row
 * label says so out loud, because the two numbers sitting one above the other
 * are exactly the shape somebody adds up.
 *
 * `at_ceiling` is the number an operator can actually act on by hand: guests
 * who have had every automatic nudge and will get no more.
 */

import React from 'react';
import { Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ReminderPressure } from '../weddingApi';
import { relativeTime } from '../weddingFormat';

interface Props {
  pressure?: ReminderPressure | null;
}

/** One server-shipped definition string, or undefined if this backend has none. */
function defText(definition: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = definition ? definition[key] : undefined;
  return typeof value === 'string' ? value : undefined;
}

interface RowProps {
  label: string;
  value: number;
  help?: string;
  tone?: 'text.primary' | 'error.main';
}

const PressureRow: React.FC<RowProps> = ({ label, value, help, tone = 'text.primary' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {help && (
        <Tooltip title={help}>
          <InfoOutlinedIcon fontSize="small" color="disabled" />
        </Tooltip>
      )}
    </Stack>
    <Typography variant="body1" sx={{ fontWeight: 700, color: tone }}>
      {value.toLocaleString()}
    </Typography>
  </Box>
);

const ReminderPressureCard: React.FC<Props> = ({ pressure }) => {
  if (!pressure) {
    return (
      <Card sx={{ borderRadius: '16px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Reminder pressure
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reminder scheduling is not reported by this version of the admin API.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const note = defText(pressure.definition, 'note');
  const nothingScheduled =
    pressure.scheduled_eligible === 0
    && pressure.overdue_now === 0
    && pressure.deadline_pass_pending === 0;

  return (
    <Card sx={{ borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Reminder pressure
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Point in time — not scoped to the date filter.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {pressure.due_24h.toLocaleString()} scheduled in the next 24 hours
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pressure.due_7d.toLocaleString()} in the next 7 days — that figure INCLUDES the 24-hour
          one, so the two must never be added.
        </Typography>

        {nothingScheduled ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The engine has nothing scheduled right now.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mb: 1.5 }}>
            <PressureRow
              label="Scheduled overall"
              value={pressure.scheduled_eligible}
              help={defText(pressure.definition, 'scheduled_predicates')}
            />
            <PressureRow
              label="Overdue right now"
              value={pressure.overdue_now}
              tone={pressure.overdue_now > 0 ? 'error.main' : 'text.primary'}
              help="Guests whose next reminder was due before now — the engine's own claim set."
            />
            <PressureRow
              label="Deadline pass pending"
              value={pressure.deadline_pass_pending}
              help={defText(pressure.definition, 'deadline_predicates')}
            />
          </Stack>
        )}

        {/*
          Written as a sentence rather than as another right-aligned figure: it
          is the one number here that describes people nobody will contact
          again, and it reads as an instruction to a human, not as a forecast.
        */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {pressure.at_ceiling.toLocaleString()} guest
            {pressure.at_ceiling === 1 ? '' : 's'} at the ceiling
          </Typography>
          <Tooltip
            title={`These contributors have had all ${pressure.max_reminders} reminders and will not be nudged again automatically. Reaching them is a manual job.`}
          >
            <InfoOutlinedIcon fontSize="small" color="disabled" />
          </Tooltip>
        </Stack>

        {pressure.oldest_overdue_at && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Oldest overdue reminder was due {relativeTime(pressure.oldest_overdue_at)}.
          </Typography>
        )}

        {/* The server's own sentence: scheduled, not sent. Rendered verbatim. */}
        {note && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            {note}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default ReminderPressureCard;
